import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import ViewListIcon from "@mui/icons-material/ViewList";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Select from "@mui/material/Select";
import TablePagination from "@mui/material/TablePagination";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import { DataGrid } from "@mui/x-data-grid";
import moment from "moment";
import ConfirmDialog from "../../components/ConfirmDialog";
import CustomSnackbar from "../../components/CustomSnackbar";
import KeywordBox from "../../components/KeywordBox";
import Logger from "../../../services/Logger";
import AdminWordWheelService from "./AdminWordWheelService";
import { getColumns } from "./getColumns";
import WordWheelPublishDialog from "./WordWheelPublishDialog";
import WordWheelReorderLevelsDialog from "./WordWheelReorderLevelsDialog";
import WordWheelSetLevelDialog from "./WordWheelSetLevelDialog";
import WordWheelDailyCalendarView from "./WordWheelDailyCalendarView";
import WordWheelTplGridCard from "./WordWheelTplGridCard";
import { WORD_WHEEL_DEFAULT_SEASON, WORD_WHEEL_SEASON_OPTIONS, extractSeasonFromRow, isJourneyPack } from "../wordWheelLevelUtils";
import AdminWordWheelPackService from "../pack/AdminWordWheelPackService";

const ALL = "ALL";
const DRAFT = "DRAFT";
const READY_TO_REVIEW = "READY_TO_REVIEW";
const REVIEWED = "REVIEWED";
const PUBLISHED = "PUBLISHED";
const STATES = [ALL, DRAFT, READY_TO_REVIEW, REVIEWED, PUBLISHED, "DISCARDED", "DISABLED"];

const KEYWORD_STORAGE_KEY = "admin.word_wheel_tpl.browse.keyword";
const STATE_STORAGE_KEY = "admin.word_wheel_tpl.browse.state";
const VIEW_STORAGE_KEY = "admin.word_wheel_tpl.browse.view";
const LANGUAGE_STORAGE_KEY = "admin.word_wheel_tpl.browse.language";
const SEASON_STORAGE_KEY = "admin.word_wheel_tpl.browse.season";
const SORT_STORAGE_KEY = "admin.word_wheel_tpl.browse.sort";
const PAGE_MODEL_STORAGE_KEY = "admin.word_wheel_tpl.browse.pageModel";
const BANNED_WORDS_FILTER_STORAGE_KEY = "admin.word_wheel_tpl.browse.bannedWordsOnly";

const LANGUAGE_OPTIONS = new Set(["all", "english", "french"]);
const PAGE_SIZE_OPTIONS = new Set([15, 30, 50, 100]);
const SEASON_NONE = "none";

const ORDER_BY_OPTIONS = [
    { value: "puzzleLevel:asc", label: "Level (low → high)" },
    { value: "puzzleLevel:desc", label: "Level (high → low)" },
    { value: "createdAt:desc", label: "Created at (newest)" },
    { value: "createdAt:asc", label: "Created at (oldest)" },
    { value: "publishedAt:desc", label: "Published at (newest)" },
    { value: "publishedAt:asc", label: "Published at (oldest)" },
];
const VALID_SORT_FIELDS = new Set(["puzzleLevel", "createdAt", "publishedAt", "title", "state", "difficultyLevel", "words"]);
const DEFAULT_SORT_MODEL = [{ field: "puzzleLevel", sort: "asc" }];

function sortModelToValue(model) {
    if (!Array.isArray(model) || model.length === 0 || !model[0]?.field) {
        return "puzzleLevel:asc";
    }
    return `${model[0].field}:${model[0].sort === "asc" ? "asc" : "desc"}`;
}

function sortValueToModel(value) {
    const raw = typeof value === "string" ? value : "";
    const [field, dir] = raw.split(":");
    if (!field || (dir !== "asc" && dir !== "desc")) {
        return [...DEFAULT_SORT_MODEL];
    }
    return [{ field, sort: dir }];
}

function loadViewMode() {
    try {
        const stored = localStorage.getItem(VIEW_STORAGE_KEY);
        if (stored === "list" || stored === "grid" || stored === "calendar") {
            return stored;
        }
    } catch (error) {
        Logger.error("Error reading word wheel tpl view from localStorage:", error);
    }
    return "list";
}

function loadKeyword() {
    try {
        const stored = localStorage.getItem(KEYWORD_STORAGE_KEY);
        return stored != null ? String(stored) : "";
    } catch (error) {
        Logger.error("Error reading word wheel tpl keyword from localStorage:", error);
        return "";
    }
}

function loadSelectedState() {
    try {
        const stored = localStorage.getItem(STATE_STORAGE_KEY);
        if (stored && STATES.includes(stored)) {
            return stored;
        }
    } catch (error) {
        Logger.error("Error reading word wheel tpl state from localStorage:", error);
    }
    return ALL;
}

function loadSelectedLanguage() {
    try {
        const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
        if (stored && LANGUAGE_OPTIONS.has(stored)) {
            return stored;
        }
    } catch (error) {
        Logger.error("Error reading word wheel tpl language from localStorage:", error);
    }
    return "all";
}

function loadSelectedSeason() {
    try {
        const stored = localStorage.getItem(SEASON_STORAGE_KEY);
        if (stored === "all" || stored === SEASON_NONE) {
            return stored;
        }
        if (stored && /^[a-z0-9_]+$/i.test(stored)) {
            return stored;
        }
    } catch (error) {
        Logger.error("Error reading word wheel tpl season from localStorage:", error);
    }
    return "all";
}

function loadSortModel() {
    try {
        const raw = localStorage.getItem(SORT_STORAGE_KEY);
        if (!raw) {
            return [...DEFAULT_SORT_MODEL];
        }
        if (typeof raw === "string" && raw.includes(":") && !raw.trim().startsWith("[")) {
            const model = sortValueToModel(raw);
            return VALID_SORT_FIELDS.has(model[0].field) ? model : [...DEFAULT_SORT_MODEL];
        }
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed) || parsed.length === 0) {
            return [...DEFAULT_SORT_MODEL];
        }
        const valid = parsed.filter(
            (item) =>
                item
                && typeof item.field === "string"
                && VALID_SORT_FIELDS.has(item.field)
                && (item.sort === "asc" || item.sort === "desc")
        );
        return valid.length > 0 ? valid : [...DEFAULT_SORT_MODEL];
    } catch (error) {
        Logger.error("Error reading word wheel tpl sort from localStorage:", error);
        return [...DEFAULT_SORT_MODEL];
    }
}

function loadPageModel() {
    try {
        const raw = localStorage.getItem(PAGE_MODEL_STORAGE_KEY);
        if (!raw) {
            return { page: 0, pageSize: 15 };
        }
        const parsed = JSON.parse(raw);
        const page = Number.isFinite(parsed?.page) && parsed.page >= 0 ? parsed.page : 0;
        const pageSize = PAGE_SIZE_OPTIONS.has(parsed?.pageSize) ? parsed.pageSize : 15;
        return { page, pageSize };
    } catch (error) {
        Logger.error("Error reading word wheel tpl page model from localStorage:", error);
        return { page: 0, pageSize: 15 };
    }
}

function loadBannedWordsOnly() {
    try {
        return localStorage.getItem(BANNED_WORDS_FILTER_STORAGE_KEY) === "true";
    } catch (error) {
        Logger.error("Error reading word wheel tpl banned-words filter from localStorage:", error);
        return false;
    }
}

function saveSearchOption(key, value) {
    try {
        if (value == null) {
            localStorage.removeItem(key);
            return;
        }
        localStorage.setItem(key, typeof value === "string" ? value : JSON.stringify(value));
    } catch (error) {
        Logger.error(`Error saving word wheel tpl search option ${key}:`, error);
    }
}

const REVIEWABLE_STATES = new Set([DRAFT, READY_TO_REVIEW]);
const PUBLISHABLE_STATES = new Set([DRAFT, READY_TO_REVIEW, REVIEWED]);

export default function AdminWordWheelTplMain() {
    const navigate = useNavigate();
    const [pageModel, setPageModel] = useState(loadPageModel);
    const [sort, setSort] = useState(loadSortModel);
    const [rowCount, setRowCount] = useState(0);
    const [rows, setRows] = useState([]);
    const [keyword, setKeyword] = useState(loadKeyword);
    const [selectedState, setSelectedState] = useState(loadSelectedState);
    const [selectedLanguage, setSelectedLanguage] = useState(loadSelectedLanguage);
    const [selectedSeason, setSelectedSeason] = useState(loadSelectedSeason);
    const [packOptions, setPackOptions] = useState(WORD_WHEEL_SEASON_OPTIONS);
    const [viewMode, setViewMode] = useState(loadViewMode);
    const [bannedWordsOnly, setBannedWordsOnly] = useState(loadBannedWordsOnly);
    const [listLoading, setListLoading] = useState(false);
    const [selectedRows, setSelectedRows] = useState([]);
    const [reload, setReload] = useState();
    const [fillingClueId, setFillingClueId] = useState(null);

    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [severity, setSeverity] = useState("success");
    const [message, setMessage] = useState("");
    const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
    const [cardPendingRemove, setCardPendingRemove] = useState(null);
    const [openReviewDialog, setOpenReviewDialog] = useState(false);
    const [openPublishDialog, setOpenPublishDialog] = useState(false);
    const [openSetLevelDialog, setOpenSetLevelDialog] = useState(false);
    const [openReorderLevelsDialog, setOpenReorderLevelsDialog] = useState(false);
    const [openRemoveDuplicatesDialog, setOpenRemoveDuplicatesDialog] = useState(false);
    const [removeDuplicatesLoading, setRemoveDuplicatesLoading] = useState(false);
    const [removeDuplicatesResult, setRemoveDuplicatesResult] = useState(null);
    const [removeDuplicatesAlsoReorder, setRemoveDuplicatesAlsoReorder] = useState(true);
    const [removeDuplicatesReorderSeason, setRemoveDuplicatesReorderSeason] = useState(WORD_WHEEL_DEFAULT_SEASON);
    const [bulkActionLoading, setBulkActionLoading] = useState(false);
    const [openRegenCluesDialog, setOpenRegenCluesDialog] = useState(false);
    const [regenCluesLoading, setRegenCluesLoading] = useState(false);
    const [regenCluesPreview, setRegenCluesPreview] = useState(null);
    const [regenCluesResult, setRegenCluesResult] = useState(null);
    const [openFillMissingDialog, setOpenFillMissingDialog] = useState(false);
    const [fillMissingLoading, setFillMissingLoading] = useState(false);
    const [fillMissingPreview, setFillMissingPreview] = useState(null);
    const [fillMissingResult, setFillMissingResult] = useState(null);
    const [openRemoveContainedDialog, setOpenRemoveContainedDialog] = useState(false);
    const [removeContainedLoading, setRemoveContainedLoading] = useState(false);
    const [removeContainedResult, setRemoveContainedResult] = useState(null);
    const [openRemoveBannedDialog, setOpenRemoveBannedDialog] = useState(false);
    const [removeBannedLoading, setRemoveBannedLoading] = useState(false);
    const [removeBannedPreview, setRemoveBannedPreview] = useState(null);
    const [removeBannedResult, setRemoveBannedResult] = useState(null);
    const [todayDaily, setTodayDaily] = useState(null);

    const selectedTemplates = useMemo(
        () => rows.filter((row) => selectedRows.includes(row.id)),
        [rows, selectedRows]
    );

    const mutableSelectedTemplates = useMemo(
        () => selectedTemplates.filter((row) => !isJourneyPack(extractSeasonFromRow(row))),
        [selectedTemplates]
    );

    const reviewEligibleTemplates = useMemo(
        () => mutableSelectedTemplates.filter((row) => REVIEWABLE_STATES.has(row.state)),
        [mutableSelectedTemplates]
    );

    const publishEligibleTemplates = useMemo(
        () => mutableSelectedTemplates.filter((row) => PUBLISHABLE_STATES.has(row.state)),
        [mutableSelectedTemplates]
    );

    const todayDailyOnPage = useMemo(
        () => Boolean(todayDaily?.id && rows.some((row) => String(row.id) === String(todayDaily.id))),
        [todayDaily, rows],
    );

    useEffect(() => {
        if (viewMode === "calendar") {
            setListLoading(false);
            return undefined;
        }
        let cancelled = false;
        setListLoading(true);
        AdminWordWheelService.requestList(
            pageModel,
            sort,
            (nextRows) => {
                if (!cancelled) setRows(nextRows);
            },
            (nextCount) => {
                if (!cancelled) setRowCount(nextCount);
            },
            keyword,
            selectedState === ALL ? null : selectedState,
            selectedLanguage,
            selectedSeason === "all" ? null : selectedSeason,
            { onlyWithBannedWords: bannedWordsOnly, includeGrid: viewMode === "grid" },
        )
            .catch((err) => {
                Logger.error("Failed to load word wheel templates:", err);
                if (!cancelled) {
                    setRows([]);
                    setRowCount(0);
                }
            })
            .finally(() => {
                if (!cancelled) setListLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [
        keyword,
        pageModel,
        sort,
        selectedState,
        selectedLanguage,
        selectedSeason,
        bannedWordsOnly,
        viewMode,
        reload,
    ]);

    useEffect(() => {
        let cancelled = false;
        AdminWordWheelPackService.list()
            .then((items) => {
                if (cancelled) {
                    return;
                }
                const codes = items.map((pack) => pack.code).filter(Boolean);
                if (codes.length > 0) {
                    setPackOptions(codes);
                }
            })
            .catch((error) => {
                Logger.error("Error loading word wheel pack filter:", error);
            });
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        let cancelled = false;
        AdminWordWheelService.previewDaily()
            .then((data) => {
                if (!cancelled) {
                    setTodayDaily(data?.id ? data : null);
                }
            })
            .catch((err) => {
                Logger.error("Failed to load today's Word Wheel daily puzzle:", err);
                if (!cancelled) setTodayDaily(null);
            });
        return () => {
            cancelled = true;
        };
    }, []);

    const handleSelectionModel = (newSelectionModel) => {
        setSelectedRows(newSelectionModel);
    };

    const handleRequestRemoveCard = (row) => {
        if (!row?.id) {
            return;
        }
        if (isJourneyPack(extractSeasonFromRow(row))) {
            handleSnackBar("warning", "Season 2026 puzzles were skipped");
            return;
        }
        setCardPendingRemove(row);
    };

    const handleConfirmRemoveCard = async () => {
        const row = cardPendingRemove;
        if (!row?.id) {
            setCardPendingRemove(null);
            return;
        }
        try {
            const result = await AdminWordWheelService.deleteTemplates([row.id]);
            if (result?.code && result.code !== "SUCCESS") {
                throw new Error(result.message || "Could not remove template");
            }
            handleSnackBar("success", "Template removed");
            setSelectedRows((prev) => (Array.isArray(prev) ? prev.filter((id) => id !== row.id) : []));
            setReload(moment());
        } catch (error) {
            Logger.error("Error removing word wheel template:", error);
            handleSnackBar("error", error?.message || "Error removing template");
        } finally {
            setCardPendingRemove(null);
        }
    };

    const handleDeleteClick = () => {
        if (selectedRows.length > 0) {
            setOpenConfirmDialog(true);
        }
    };

    const handleConfirmDelete = async () => {
        const skipped = selectedTemplates.length - mutableSelectedTemplates.length;
        if (mutableSelectedTemplates.length === 0) {
            handleSnackBar("warning", "Season 2026 puzzles were skipped");
            setOpenConfirmDialog(false);
            return;
        }
        try {
            await AdminWordWheelService.deleteTemplates(mutableSelectedTemplates.map((row) => row.id));
            const skippedNote = skipped > 0 ? `; ${skipped} Season 2026 puzzle(s) skipped` : "";
            handleSnackBar("success", `${mutableSelectedTemplates.length} template(s) deleted successfully${skippedNote}`);
            setReload(moment());
            setSelectedRows([]);
            setOpenConfirmDialog(false);
        } catch (error) {
            Logger.error("Error deleting word wheel templates:", error);
            handleSnackBar("error", "Error deleting templates");
        }
    };

    const formatBulkResultMessage = (actionLabel, result) => {
        const parts = [];
        if (result.succeeded.length > 0) {
            parts.push(`${result.succeeded.length} ${actionLabel}`);
        }
        if (result.skipped.length > 0) {
            parts.push(`${result.skipped.length} skipped (already ${actionLabel === "published" ? "published" : "reviewed or published"})`);
        }
        if (result.failed.length > 0) {
            const firstError = result.failed[0]?.error || "";
            const allSameError = result.failed.every((item) => item?.error === firstError);
            if (allSameError && firstError.includes("season catalog")) {
                parts.push(
                    `${result.failed.length} failed: word list overlaps an existing season puzzle. Use Remove duplicates, then retry.`
                );
            } else {
                parts.push(`${result.failed.length} failed${firstError ? `: ${firstError}` : ""}`);
            }
        }
        return parts.join("; ");
    };

    const handleConfirmReview = async () => {
        setBulkActionLoading(true);
        try {
            const result = await AdminWordWheelService.promoteTemplatesToReviewed(reviewEligibleTemplates);
            const severity = result.failed.length > 0 ? "warning" : "success";
            handleSnackBar(severity, formatBulkResultMessage("marked reviewed", result));
            if (result.succeeded.length > 0) {
                setReload(moment());
            }
            if (result.failed.length === 0) {
                setSelectedRows([]);
            }
            setOpenReviewDialog(false);
        } catch (error) {
            Logger.error("Error marking word wheel templates as reviewed:", error);
            handleSnackBar("error", "Error marking templates as reviewed");
        } finally {
            setBulkActionLoading(false);
        }
    };

    const handleConfirmPublish = async (options) => {
        setBulkActionLoading(true);
        try {
            let templatesToPublish = publishEligibleTemplates;
            if (options.publishAllDraft) {
                templatesToPublish = await AdminWordWheelService.fetchTemplatesByState(
                    DRAFT,
                    selectedLanguage
                );
            }
            const beforeSkip = templatesToPublish.length;
            templatesToPublish = templatesToPublish.filter((row) => !isJourneyPack(extractSeasonFromRow(row)));
            const skippedJourney = beforeSkip - templatesToPublish.length;
            if (templatesToPublish.length === 0) {
                handleSnackBar("warning", skippedJourney > 0 ? "Season 2026 puzzles were skipped" : "No templates to publish");
                return;
            }
            const result = await AdminWordWheelService.promoteTemplatesToPublished(
                templatesToPublish,
                options
            );
            const severity = result.failed.length > 0 ? "warning" : "success";
            const skippedNote = skippedJourney > 0 ? `; ${skippedJourney} Season 2026 puzzle(s) skipped` : "";
            handleSnackBar(severity, `${formatBulkResultMessage("published", result)}${skippedNote}`);
            if (result.succeeded.length > 0) {
                setReload(moment());
            }
            if (result.failed.length === 0) {
                setSelectedRows([]);
            }
            setOpenPublishDialog(false);
        } catch (error) {
            Logger.error("Error publishing word wheel templates:", error);
            handleSnackBar("error", "Error publishing templates");
        } finally {
            setBulkActionLoading(false);
        }
    };

    const handleConfirmSetLevel = async ({ puzzleLevel, templates }) => {
        setBulkActionLoading(true);
        try {
            const result = await AdminWordWheelService.updateTemplateLevels(templates, puzzleLevel);
            const severity = result.failed.length > 0 ? "warning" : "success";
            const message = result.failed.length > 0
                ? `${result.succeeded.length} updated; ${result.failed.length} failed: ${result.failed[0]?.error || ""}`
                : `${result.succeeded.length} template(s) updated to level ${puzzleLevel}`;
            handleSnackBar(severity, message);
            if (result.succeeded.length > 0) {
                setReload(moment());
            }
            setOpenSetLevelDialog(false);
        } catch (error) {
            Logger.error("Error setting word wheel template levels:", error);
            handleSnackBar("error", "Error setting template levels");
        } finally {
            setBulkActionLoading(false);
        }
    };

    const handleSnackBar = (nextSeverity, nextMessage) => {
        setOpenSnackbar(true);
        setSeverity(nextSeverity);
        setMessage(nextMessage);
    };

    const handleRemoveDuplicatesClose = () => {
        const finished = removeDuplicatesResult && !removeDuplicatesResult.error;
        setOpenRemoveDuplicatesDialog(false);
        setRemoveDuplicatesResult(null);
        if (finished) {
            setSelectedRows([]);
            setReload(moment());
        }
    };

    const handleRemoveDuplicatesRun = async () => {
        setRemoveDuplicatesLoading(true);
        setRemoveDuplicatesResult(null);
        try {
            const result = await AdminWordWheelService.removeSubsetDuplicates(selectedLanguage, false);
            if (result?.code !== "SUCCESS") {
                setRemoveDuplicatesResult(result);
                return;
            }

            let reorderNote = "";
            if (removeDuplicatesAlsoReorder) {
                const language =
                    selectedLanguage && selectedLanguage !== "all" ? selectedLanguage : "english";
                try {
                    const reorder = await AdminWordWheelService.assignSeasonLevels(
                        removeDuplicatesReorderSeason,
                        language
                    );
                    reorderNote = ` Re-ordered ${reorder?.updated ?? 0} level(s) in ${removeDuplicatesReorderSeason}.`;
                    result.reorder = reorder;
                } catch (reorderErr) {
                    Logger.error("Failed to re-order levels after remove duplicates:", reorderErr);
                    reorderNote = ` Levels not re-ordered: ${reorderErr?.message || "unknown error"}.`;
                    result.reorderError = reorderErr?.message || String(reorderErr);
                }
            }

            setRemoveDuplicatesResult(result);
            handleSnackBar(
                "success",
                `Deleted ${result.deleted ?? 0} duplicate template(s) (${result.kept ?? 0} kept).${reorderNote}`
            );
        } catch (err) {
            Logger.error("Failed to remove word wheel duplicate templates:", err);
            setRemoveDuplicatesResult({ error: err?.message || "Failed to remove duplicates." });
        } finally {
            setRemoveDuplicatesLoading(false);
        }
    };

    const handleRemoveBannedClose = () => {
        const finished = removeBannedResult && !removeBannedResult.error;
        setOpenRemoveBannedDialog(false);
        setRemoveBannedPreview(null);
        setRemoveBannedResult(null);
        if (finished) {
            setReload(moment());
        }
    };

    const handleRemoveContainedClose = () => {
        const finished = removeContainedResult && !removeContainedResult.error;
        setOpenRemoveContainedDialog(false);
        setRemoveContainedResult(null);
        if (finished) {
            setReload(moment());
        }
    };

    const handleRemoveContainedRun = async () => {
        setRemoveContainedLoading(true);
        setRemoveContainedResult(null);
        try {
            const result = await AdminWordWheelService.removeContainedWords(selectedLanguage, false);
            setRemoveContainedResult(result);
            if (result?.code === "SUCCESS") {
                handleSnackBar(
                    "success",
                    `Removed contained words from ${result.updated ?? 0} puzzle(s)`
                        + ` (${result.wordsRemoved ?? 0} words).`
                );
            }
        } catch (err) {
            Logger.error("Failed to remove contained word placements:", err);
            setRemoveContainedResult({ error: err?.message || "Failed to remove contained words." });
        } finally {
            setRemoveContainedLoading(false);
        }
    };

    useEffect(() => {
        if (!openRemoveBannedDialog) {
            return undefined;
        }
        let cancelled = false;
        const loadPreview = async () => {
            setRemoveBannedLoading(true);
            setRemoveBannedPreview(null);
            setRemoveBannedResult(null);
            try {
                const result = await AdminWordWheelService.removeBannedWords(selectedLanguage, true);
                if (!cancelled) {
                    setRemoveBannedPreview(result);
                }
            } catch (err) {
                Logger.error("Failed to preview banned-word removal:", err);
                if (!cancelled) {
                    setRemoveBannedPreview({ error: err?.message || "Failed to load preview." });
                }
            } finally {
                if (!cancelled) {
                    setRemoveBannedLoading(false);
                }
            }
        };
        loadPreview();
        return () => {
            cancelled = true;
        };
    }, [openRemoveBannedDialog, selectedLanguage]);

    const handleRemoveBannedConfirm = async () => {
        setRemoveBannedLoading(true);
        setRemoveBannedResult(null);
        try {
            const result = await AdminWordWheelService.removeBannedWords(selectedLanguage, false);
            setRemoveBannedResult(result);
            if (result?.code === "SUCCESS") {
                handleSnackBar(
                    "success",
                    `Removed banned words from ${result.updated ?? 0} puzzle(s)`
                        + ` (${result.wordsRemoved ?? 0} words).`
                );
            }
        } catch (err) {
            Logger.error("Failed to remove banned words:", err);
            setRemoveBannedResult({ error: err?.message || "Failed to remove banned words." });
        } finally {
            setRemoveBannedLoading(false);
        }
    };

    const handleRegenCluesClose = () => {
        setOpenRegenCluesDialog(false);
        setRegenCluesPreview(null);
        setRegenCluesResult(null);
    };

    const handleRegenCluesConfirm = async () => {
        setRegenCluesLoading(true);
        setRegenCluesResult(null);
        try {
            const result = await AdminWordWheelService.regenerateDisplayClues(selectedLanguage, false);
            setRegenCluesResult(result);
            if (result?.code === "SUCCESS") {
                setReload(moment());
                const remaining = result?.remaining ?? 0;
                handleSnackBar(
                    remaining > 0 ? "warning" : "success",
                    `Regenerated ${result?.regenerated ?? 0} clue set(s)`
                        + (result?.failed > 0 ? `, ${result.failed} failed` : "")
                        + (remaining > 0 ? `. ${remaining} still missing — run again.` : ".")
                );
            }
        } catch (err) {
            Logger.error("Failed to regenerate display clues:", err);
            setRegenCluesResult({ error: err?.message || "Failed to regenerate clues." });
        } finally {
            setRegenCluesLoading(false);
        }
    };

    const handleFillMissingClose = () => {
        setOpenFillMissingDialog(false);
        setFillMissingPreview(null);
        setFillMissingResult(null);
    };

    const handleFillMissingConfirm = async () => {
        setFillMissingLoading(true);
        setFillMissingResult(null);
        try {
            const result = await AdminWordWheelService.fillAllMissingDisplayCluesFromDictionary(
                selectedLanguage,
                false,
            );
            setFillMissingResult(result);
            if (result?.code === "SUCCESS") {
                setReload(moment());
                const remaining = result?.remaining ?? 0;
                handleSnackBar(
                    remaining > 0 ? "warning" : "success",
                    `Filled ${result?.filled ?? 0} clue(s) on ${result?.updated ?? 0} puzzle(s)`
                        + (remaining > 0
                            ? `. ${remaining} still missing a dictionary meaning.`
                            : "."),
                );
            }
        } catch (err) {
            Logger.error("Failed to generate missing display clues:", err);
            setFillMissingResult({ error: err?.message || "Failed to generate missing clues." });
        } finally {
            setFillMissingLoading(false);
        }
    };

    const handleKeywordChange = (value) => {
        setKeyword(value);
        saveSearchOption(KEYWORD_STORAGE_KEY, value);
    };

    const handleStateChange = (value) => {
        setSelectedState(value);
        saveSearchOption(STATE_STORAGE_KEY, value);
        setPageModel((prev) => {
            const next = { ...prev, page: 0 };
            saveSearchOption(PAGE_MODEL_STORAGE_KEY, next);
            return next;
        });
    };

    const handleLanguageChange = (value) => {
        setSelectedLanguage(value);
        saveSearchOption(LANGUAGE_STORAGE_KEY, value);
        setPageModel((prev) => {
            const next = { ...prev, page: 0 };
            saveSearchOption(PAGE_MODEL_STORAGE_KEY, next);
            return next;
        });
    };

    const handleSeasonChange = (value) => {
        setSelectedSeason(value);
        saveSearchOption(SEASON_STORAGE_KEY, value);
        setPageModel((prev) => {
            const next = { ...prev, page: 0 };
            saveSearchOption(PAGE_MODEL_STORAGE_KEY, next);
            return next;
        });
    };

    const handleGridLayoutSaved = (tplId, coords) => {
        setRows((prev) => prev.map((row) => (
            row.id === tplId
                ? { ...row, filledCoordinates: JSON.stringify(coords) }
                : row
        )));
    };

    const handleFillMissingClues = async (row) => {
        if (!row?.id || fillingClueId) {
            return;
        }
        setFillingClueId(row.id);
        try {
            const result = await AdminWordWheelService.fillDisplayCluesFromDictionary(row.id);
            const updated = result?.wordWheelTpl;
            const filled = Number(result?.filled) || 0;
            const stillMissing = Array.isArray(result?.stillMissing) ? result.stillMissing : [];
            if (updated) {
                setRows((prev) => prev.map((item) => (
                    item.id === row.id
                        ? { ...item, displayClue: updated.displayClue }
                        : item
                )));
            }
            if (filled > 0 && stillMissing.length === 0) {
                handleSnackBar("success", `Filled ${filled} clue(s) from the dictionary.`);
            } else if (filled > 0) {
                handleSnackBar(
                    "warning",
                    `Filled ${filled} clue(s). Still missing: ${stillMissing.join(", ")}.`
                );
            } else if (stillMissing.length > 0) {
                handleSnackBar(
                    "warning",
                    `No dictionary meanings found for: ${stillMissing.join(", ")}.`
                );
            } else {
                handleSnackBar("info", "All words already have clues.");
            }
        } catch (error) {
            Logger.error("Failed to fill word wheel clues from dictionary:", error);
            handleSnackBar("error", error?.message || "Failed to fill clues from the dictionary.");
        } finally {
            setFillingClueId(null);
        }
    };

    const handleBannedWordsOnlyChange = (checked) => {
        setBannedWordsOnly(checked);
        saveSearchOption(BANNED_WORDS_FILTER_STORAGE_KEY, checked ? "true" : "false");
        setPageModel((prev) => {
            const next = { ...prev, page: 0 };
            saveSearchOption(PAGE_MODEL_STORAGE_KEY, next);
            return next;
        });
        setSelectedRows([]);
    };

    const handlePageModelChange = (model) => {
        setPageModel(model);
        saveSearchOption(PAGE_MODEL_STORAGE_KEY, model);
    };

    const handleViewModeChange = (_event, next) => {
        if (!next) {
            return;
        }
        setViewMode(next);
        saveSearchOption(VIEW_STORAGE_KEY, next);
    };

    const handleGridCardSelect = (row, checked) => {
        setSelectedRows((prev) => {
            const current = Array.isArray(prev) ? prev : [];
            if (checked) {
                return current.includes(row.id) ? current : [...current, row.id];
            }
            return current.filter((id) => id !== row.id);
        });
    };

    const allGridCardsSelected = rows.length > 0
        && rows.every((row) => selectedRows.includes(row.id));
    const someGridCardsSelected = rows.some((row) => selectedRows.includes(row.id));

    const handleSelectAllGridCards = (checked) => {
        const pageIds = rows.map((row) => row.id);
        setSelectedRows((prev) => {
            const current = Array.isArray(prev) ? prev : [];
            if (checked) {
                const next = new Set(current);
                pageIds.forEach((id) => next.add(id));
                return Array.from(next);
            }
            const pageIdSet = new Set(pageIds);
            return current.filter((id) => !pageIdSet.has(id));
        });
    };

    const handleSortModelChange = (model) => {
        const next = Array.isArray(model) && model.length > 0 ? model : [...DEFAULT_SORT_MODEL];
        setSort(next);
        saveSearchOption(SORT_STORAGE_KEY, next);
    };

    const handleOrderByChange = (value) => {
        const next = sortValueToModel(value);
        setSort(next);
        saveSearchOption(SORT_STORAGE_KEY, next);
        setPageModel((prev) => {
            const updated = { ...prev, page: 0 };
            saveSearchOption(PAGE_MODEL_STORAGE_KEY, updated);
            return updated;
        });
    };

    const handleCell = (cell, event) => {
        if (cell.field === "__check__") {
            return;
        }
        if (event?.target?.closest?.("button")) {
            return;
        }
        navigate(`/admin/puzzle/word_wheel_tpl/detail/${cell.id}`);
    };

    return (
        <Box sx={{ p: 2 }}>
            <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                    <Button
                        variant="contained"
                        color="success"
                        onClick={() => setOpenReviewDialog(true)}
                        disabled={reviewEligibleTemplates.length === 0 || bulkActionLoading}
                        sx={{ minWidth: 140, minHeight: 40 }}
                        startIcon={bulkActionLoading ? <CircularProgress size={16} color="inherit" /> : null}
                    >
                        Mark Reviewed ({reviewEligibleTemplates.length})
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => setOpenPublishDialog(true)}
                        disabled={bulkActionLoading}
                        sx={{ minWidth: 120, minHeight: 40 }}
                        startIcon={bulkActionLoading ? <CircularProgress size={16} color="inherit" /> : null}
                    >
                        Publish{publishEligibleTemplates.length > 0 ? ` (${publishEligibleTemplates.length})` : ""}
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleDeleteClick}
                        disabled={mutableSelectedTemplates.length === 0 || bulkActionLoading}
                        sx={{ minWidth: 100, minHeight: 40 }}
                    >
                        Delete ({mutableSelectedTemplates.length})
                    </Button>
                </Box>
            </Paper>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
                Word Wheel Templates
            </Typography>
            <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap", alignItems: "center" }}>
                <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={viewMode}
                    onChange={handleViewModeChange}
                    aria-label="Browse view"
                >
                    <ToggleButton value="list" aria-label="List view" title="List" sx={{ px: 1, minWidth: 44, minHeight: 40 }}>
                        <ViewListIcon fontSize="small" />
                    </ToggleButton>
                    <ToggleButton value="grid" aria-label="Grid view" title="Grid" sx={{ px: 1, minWidth: 44, minHeight: 40 }}>
                        <ViewModuleIcon fontSize="small" />
                    </ToggleButton>
                    <ToggleButton value="calendar" aria-label="Calendar view" title="Calendar" sx={{ px: 1, minWidth: 44, minHeight: 40 }}>
                        <CalendarMonthIcon fontSize="small" />
                    </ToggleButton>
                </ToggleButtonGroup>
                {viewMode === "calendar" ? null : (
                <>
                <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel>Language</InputLabel>
                    <Select
                        label="Language"
                        value={selectedLanguage}
                        onChange={(e) => handleLanguageChange(e.target.value)}
                    >
                        <MenuItem value="all">All</MenuItem>
                        <MenuItem value="english">English</MenuItem>
                        <MenuItem value="french">French</MenuItem>
                    </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel>Pack</InputLabel>
                    <Select
                        label="Pack"
                        value={selectedSeason}
                        onChange={(e) => handleSeasonChange(e.target.value)}
                    >
                        <MenuItem value="all">All</MenuItem>
                        <MenuItem value={SEASON_NONE}>No pack</MenuItem>
                        {packOptions.map((season) => (
                            <MenuItem key={season} value={season}>{season}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel>State</InputLabel>
                    <Select
                        label="State"
                        value={selectedState}
                        onChange={(e) => handleStateChange(e.target.value)}
                    >
                        {STATES.map((s) => (
                            <MenuItem key={s} value={s}>{s}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel id="ww-tpl-order-by-label">Order by</InputLabel>
                    <Select
                        labelId="ww-tpl-order-by-label"
                        label="Order by"
                        value={sortModelToValue(sort)}
                        onChange={(e) => handleOrderByChange(e.target.value)}
                    >
                        {ORDER_BY_OPTIONS.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <KeywordBox callback={handleKeywordChange} initialValue={keyword} />
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={bannedWordsOnly}
                            onChange={(e) => handleBannedWordsOnlyChange(e.target.checked)}
                            disabled={listLoading}
                        />
                    }
                    label="Banned words"
                />
                {bannedWordsOnly && listLoading ? (
                    <CircularProgress size={18} />
                ) : null}
                </>
                )}
            </Box>
            {viewMode !== "calendar" && todayDaily?.id && !todayDailyOnPage ? (
                <Alert
                    severity="info"
                    sx={{ mb: 2 }}
                    action={
                        <Button
                            color="inherit"
                            size="small"
                            onClick={() => navigate(`/admin/puzzle/word_wheel_tpl/detail/${todayDaily.id}`)}
                        >
                            Open
                        </Button>
                    }
                >
                    Today’s phone puzzle is the daily (non-season) catalog, not “published on this date.”
                    {todayDaily.title ? ` Current daily: “${todayDaily.title}”.` : ""}
                    {" "}Switch Season to Non-season to see it in the grid.
                </Alert>
            ) : null}
            {viewMode === "calendar" ? (
                <WordWheelDailyCalendarView
                    onOpen={(tpl) => navigate(`/admin/puzzle/word_wheel_tpl/detail/${tpl.id}`)}
                    onLayoutSaved={handleGridLayoutSaved}
                    onNotify={handleSnackBar}
                />
            ) : viewMode === "grid" ? (
                <Box>
                    {rows.length > 0 ? (
                        <FormControlLabel
                            sx={{ mb: 1, minHeight: 44 }}
                            control={
                                <Checkbox
                                    checked={allGridCardsSelected}
                                    indeterminate={someGridCardsSelected && !allGridCardsSelected}
                                    onChange={(event) => handleSelectAllGridCards(event.target.checked)}
                                    disabled={listLoading}
                                />
                            }
                            label="Select all"
                        />
                    ) : null}
                    {listLoading ? (
                        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                            <CircularProgress size={32} />
                        </Box>
                    ) : rows.length === 0 ? (
                        <Paper sx={{ py: 4, textAlign: "center" }}>
                            <Typography color="text.secondary">No templates found.</Typography>
                        </Paper>
                    ) : (
                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 220px), 1fr))",
                                gap: 2,
                            }}
                        >
                            {rows.map((row) => (
                                <WordWheelTplGridCard
                                    key={row.id}
                                    row={row}
                                    selected={Array.isArray(selectedRows) && selectedRows.includes(row.id)}
                                    onSelect={handleGridCardSelect}
                                    onOpen={(tpl) => navigate(`/admin/puzzle/word_wheel_tpl/detail/${tpl.id}`)}
                                    onLayoutSaved={handleGridLayoutSaved}
                                    onNotify={handleSnackBar}
                                    onFillMissingClues={handleFillMissingClues}
                                    onRemove={handleRequestRemoveCard}
                                    fillingClues={fillingClueId === row.id}
                                    isTodayDaily={Boolean(todayDaily?.id)
                                        && String(row.id) === String(todayDaily.id)}
                                />
                            ))}
                        </Box>
                    )}
                    <TablePagination
                        component="div"
                        count={rowCount}
                        page={pageModel.page}
                        onPageChange={(_event, page) => handlePageModelChange({ ...pageModel, page })}
                        rowsPerPage={pageModel.pageSize}
                        onRowsPerPageChange={(event) => handlePageModelChange({
                            page: 0,
                            pageSize: Number(event.target.value),
                        })}
                        rowsPerPageOptions={[15, 30, 50, 100]}
                    />
                </Box>
            ) : (
                <DataGrid
                    rows={rows}
                    columns={getColumns({
                        onFillMissingClues: handleFillMissingClues,
                        fillingClueId,
                    })}
                    rowCount={rowCount}
                    loading={listLoading}
                    paginationMode="server"
                    sortingMode="server"
                    paginationModel={pageModel}
                    onPaginationModelChange={handlePageModelChange}
                    sortModel={sort}
                    onSortModelChange={handleSortModelChange}
                    pageSizeOptions={[15, 30, 50, 100]}
                    autoHeight
                    checkboxSelection
                    rowSelectionModel={selectedRows}
                    onRowSelectionModelChange={handleSelectionModel}
                    onCellClick={handleCell}
                    hideFooterSelectedRowCount
                    sx={{ "& .MuiDataGrid-row": { cursor: "pointer" } }}
                />
            )}

            <CustomSnackbar open={openSnackbar} setOpen={setOpenSnackbar} severity={severity} message={message} />

            <ConfirmDialog
                open={openConfirmDialog}
                setOpen={setOpenConfirmDialog}
                callback={handleConfirmDelete}
                title="Confirm Delete"
                message={[
                    `Are you sure you want to delete ${mutableSelectedTemplates.length} selected template(s)? This action cannot be undone.`,
                    "Season 2026 puzzles are skipped. Selected template(s) and related play sessions will be deleted.",
                ]}
                targetName={`${mutableSelectedTemplates.length} selected template(s)`}
            />

            <ConfirmDialog
                open={Boolean(cardPendingRemove)}
                setOpen={(open) => {
                    if (!open) {
                        setCardPendingRemove(null);
                    }
                }}
                callback={handleConfirmRemoveCard}
                title="Remove template"
                message={[
                    `Remove “${cardPendingRemove?.title || "this template"}”? This cannot be undone.`,
                    "The template and its play sessions will be deleted.",
                ]}
                targetName={cardPendingRemove?.title || "this template"}
                okText="Remove"
            />

            <WordWheelPublishDialog
                open={openPublishDialog}
                onClose={() => setOpenPublishDialog(false)}
                onConfirm={handleConfirmPublish}
                templates={publishEligibleTemplates}
                language={selectedLanguage}
                loading={bulkActionLoading}
                allowPublishAllDraft
            />

            <WordWheelSetLevelDialog
                open={openSetLevelDialog}
                onClose={() => setOpenSetLevelDialog(false)}
                onConfirm={handleConfirmSetLevel}
                templates={selectedTemplates}
                loading={bulkActionLoading}
            />

            <WordWheelReorderLevelsDialog
                open={openReorderLevelsDialog}
                onClose={() => setOpenReorderLevelsDialog(false)}
                initialSeason={
                    selectedSeason !== "all" ? selectedSeason : WORD_WHEEL_DEFAULT_SEASON
                }
                initialLanguage={selectedLanguage}
                onReordered={(result) => {
                    setReload(moment());
                    handleSnackBar(
                        "success",
                        `Re-ordered ${result?.updated ?? 0} puzzle level(s)`
                    );
                }}
            />

            <Dialog
                open={openRegenCluesDialog}
                onClose={regenCluesLoading ? undefined : handleRegenCluesClose}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Regenerate missing display clues</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        Fills all empty or incomplete per-word clues for READY_TO_REVIEW / REVIEWED / PUBLISHED{" "}
                        {selectedLanguage === "all" ? "english" : selectedLanguage} puzzles in one run.
                        Reuses dictionary and existing puzzle clues when available; Bedrock only for words that
                        still have no clue. Existing clues are also synced into the dictionary. This can take a
                        long time for large catalogs.
                    </DialogContentText>
                    {regenCluesLoading && (
                        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                            <CircularProgress size={28} />
                        </Box>
                    )}
                    {(regenCluesPreview?.error || regenCluesResult?.error) && (
                        <Alert severity="error" sx={{ mt: 1 }}>
                            {regenCluesPreview?.error || regenCluesResult?.error}
                        </Alert>
                    )}
                    {regenCluesPreview && !regenCluesPreview.error && !regenCluesResult && (
                        <Alert severity="info" sx={{ mt: 1 }}>
                            Incomplete puzzles: {regenCluesPreview.missing ?? 0}
                            {(regenCluesPreview.missing ?? 0) === 0
                                ? ". Will only sync existing clues into the dictionary (no Bedrock)."
                                : `. This run will process all ${regenCluesPreview.willProcess
                                    ?? regenCluesPreview.missing} incomplete puzzle(s).`}
                        </Alert>
                    )}
                    {regenCluesResult && !regenCluesResult.error && regenCluesResult.code === "SUCCESS" && (
                        <Alert
                            severity={(regenCluesResult.remaining ?? 0) > 0 || (regenCluesResult.failed ?? 0) > 0
                                ? "warning"
                                : "success"}
                            sx={{ mt: 1 }}
                        >
                            Regenerated {regenCluesResult.regenerated ?? 0}
                            {(regenCluesResult.failed ?? 0) > 0 ? `, failed ${regenCluesResult.failed}` : ""}
                            {(regenCluesResult.remaining ?? 0) > 0
                                ? `. About ${regenCluesResult.remaining} still missing — run again.`
                                : ". Existing clues synced to dictionary; nothing left incomplete."}
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleRegenCluesClose} disabled={regenCluesLoading}>
                        {regenCluesResult && !regenCluesResult.error ? "Close" : "Cancel"}
                    </Button>
                    {!regenCluesResult?.code && !regenCluesPreview?.error && (
                        <Button
                            variant="contained"
                            onClick={handleRegenCluesConfirm}
                            disabled={regenCluesLoading || !regenCluesPreview}
                        >
                            {(regenCluesPreview?.missing ?? 0) === 0
                                ? "Sync to dictionary"
                                : "Regenerate all"}
                        </Button>
                    )}
                    {regenCluesResult?.code === "SUCCESS" && (regenCluesResult.remaining ?? 0) > 0 && (
                        <Button
                            variant="contained"
                            onClick={() => {
                                setRegenCluesResult(null);
                                handleRegenCluesConfirm();
                            }}
                            disabled={regenCluesLoading}
                        >
                            Retry remaining
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            <Dialog
                open={openFillMissingDialog}
                onClose={fillMissingLoading ? undefined : handleFillMissingClose}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Generate missing clues</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        Fills blank clues from the dictionary first meaning only (no Bedrock). Same as clicking
                        the red flag on each puzzle. Existing clues stay as they are. Language:{" "}
                        {selectedLanguage === "all" ? "all" : selectedLanguage}.
                    </DialogContentText>
                    {fillMissingLoading && (
                        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                            <CircularProgress size={28} />
                        </Box>
                    )}
                    {(fillMissingPreview?.error || fillMissingResult?.error) && (
                        <Alert severity="error" sx={{ mt: 1 }}>
                            {fillMissingPreview?.error || fillMissingResult?.error}
                        </Alert>
                    )}
                    {fillMissingPreview && !fillMissingPreview.error && !fillMissingResult && (
                        <Alert severity="info" sx={{ mt: 1 }}>
                            Puzzles with missing clues: {fillMissingPreview.missing ?? 0}
                            {(fillMissingPreview.missing ?? 0) === 0
                                ? ". Nothing to fill."
                                : `. This run will fill dictionary meanings on ${fillMissingPreview.willProcess
                                    ?? fillMissingPreview.missing} puzzle(s).`}
                        </Alert>
                    )}
                    {fillMissingResult && !fillMissingResult.error && fillMissingResult.code === "SUCCESS" && (
                        <Alert
                            severity={(fillMissingResult.remaining ?? 0) > 0 ? "warning" : "success"}
                            sx={{ mt: 1 }}
                        >
                            Filled {fillMissingResult.filled ?? 0} clue(s) on {fillMissingResult.updated ?? 0} puzzle(s)
                            {(fillMissingResult.remaining ?? 0) > 0
                                ? `. ${fillMissingResult.remaining} still have words with no dictionary meaning.`
                                : "."}
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleFillMissingClose} disabled={fillMissingLoading}>
                        {fillMissingResult && !fillMissingResult.error ? "Close" : "Cancel"}
                    </Button>
                    {!fillMissingResult?.code && !fillMissingPreview?.error && (
                        <Button
                            variant="contained"
                            onClick={handleFillMissingConfirm}
                            disabled={
                                fillMissingLoading
                                || !fillMissingPreview
                                || (fillMissingPreview.missing ?? 0) === 0
                            }
                            sx={{ minHeight: 40 }}
                        >
                            Generate missing clues
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            <Dialog
                open={openRemoveDuplicatesDialog}
                onClose={removeDuplicatesLoading ? undefined : handleRemoveDuplicatesClose}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Remove duplicate word wheels</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        Removes puzzles that cannot stay in the season catalog: same word list, subset duplicates,
                        and drafts that overlap a published/reviewed season puzzle (e.g. &quot;already includes all
                        words from…&quot;). Keeps one puzzle per group (prefers more words, then
                        published/reviewed, then oldest). Published puzzles with play history are skipped.
                    </DialogContentText>
                    {!removeDuplicatesResult && (
                        <Box sx={{ mb: 1.5 }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={removeDuplicatesAlsoReorder}
                                        onChange={(e) => setRemoveDuplicatesAlsoReorder(e.target.checked)}
                                        disabled={removeDuplicatesLoading}
                                    />
                                }
                                label="Also re-order season levels after delete"
                            />
                            {removeDuplicatesAlsoReorder && (
                                <FormControl size="small" sx={{ minWidth: 200, mt: 1, display: "block" }}>
                                    <InputLabel id="ww-dedupe-reorder-season-label">Season</InputLabel>
                                    <Select
                                        labelId="ww-dedupe-reorder-season-label"
                                        label="Season"
                                        value={removeDuplicatesReorderSeason}
                                        onChange={(e) => setRemoveDuplicatesReorderSeason(e.target.value)}
                                        disabled={removeDuplicatesLoading}
                                    >
                                        {WORD_WHEEL_SEASON_OPTIONS.map((season) => (
                                            <MenuItem key={season} value={season}>
                                                {season}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            )}
                        </Box>
                    )}
                    {removeDuplicatesLoading && (
                        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                            <CircularProgress size={28} />
                        </Box>
                    )}
                    {removeDuplicatesResult?.error && (
                        <Alert severity="error" sx={{ mt: 1 }}>
                            {removeDuplicatesResult.error}
                        </Alert>
                    )}
                    {removeDuplicatesResult
                        && !removeDuplicatesResult.error
                        && removeDuplicatesResult.code === "SUCCESS" && (
                        <Alert severity="success" sx={{ mt: 1 }}>
                            Deleted {removeDuplicatesResult.deleted ?? 0} templates (
                            {removeDuplicatesResult.kept ?? 0} kept,{" "}
                            {removeDuplicatesResult.skipped ?? 0} skipped).
                            {removeDuplicatesResult.reorder && (
                                <> Re-ordered {removeDuplicatesResult.reorder.updated ?? 0} level(s)
                                {" "}in {removeDuplicatesReorderSeason}.</>
                            )}
                            {removeDuplicatesResult.reorderError && (
                                <> Levels not re-ordered: {removeDuplicatesResult.reorderError}</>
                            )}
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleRemoveDuplicatesClose} disabled={removeDuplicatesLoading}>
                        {removeDuplicatesResult && !removeDuplicatesResult.error ? "Close" : "Cancel"}
                    </Button>
                    {!removeDuplicatesResult?.code && (
                        <Button
                            variant="contained"
                            color="error"
                            onClick={handleRemoveDuplicatesRun}
                            disabled={removeDuplicatesLoading}
                            startIcon={removeDuplicatesLoading ? <CircularProgress size={16} color="inherit" /> : null}
                        >
                            Run
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            <Dialog
                open={openRemoveBannedDialog}
                onClose={removeBannedLoading ? undefined : handleRemoveBannedClose}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Remove banned words</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        Removes words from categories{" "}
                        <strong>profanity, violence, drugs, offensive, inappropriate, other</strong>{" "}
                        from each puzzle, then <strong>re-arranges</strong> the remaining words on the grid.
                        <strong> high_frequency</strong> is kept. Needs at least 3 words left to rearrange.
                        Language: {selectedLanguage === "all" ? "english" : selectedLanguage}.
                    </DialogContentText>
                    {removeBannedLoading && (
                        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                            <CircularProgress size={28} />
                        </Box>
                    )}
                    {removeBannedPreview?.error && (
                        <Alert severity="error" sx={{ mt: 1 }}>
                            {removeBannedPreview.error}
                        </Alert>
                    )}
                    {removeBannedPreview
                        && !removeBannedPreview.error
                        && removeBannedPreview.code === "SUCCESS"
                        && !removeBannedResult && (
                        <Alert severity="info" sx={{ mt: 1 }}>
                            Would update {removeBannedPreview.updated ?? 0} of{" "}
                            {removeBannedPreview.total ?? 0} puzzles
                            ({removeBannedPreview.wordsRemoved ?? 0} words
                            {removeBannedPreview.skipped
                                ? `, ${removeBannedPreview.skipped} skipped`
                                : ""}
                            ).
                            {Array.isArray(removeBannedPreview.samples)
                                && removeBannedPreview.samples.length > 0 && (
                                <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
                                    {removeBannedPreview.samples.slice(0, 8).map((sample) => (
                                        <li key={sample}>
                                            <Typography variant="caption">{sample}</Typography>
                                        </li>
                                    ))}
                                </Box>
                            )}
                        </Alert>
                    )}
                    {removeBannedResult?.error && (
                        <Alert severity="error" sx={{ mt: 1 }}>
                            {removeBannedResult.error}
                        </Alert>
                    )}
                    {removeBannedResult
                        && !removeBannedResult.error
                        && removeBannedResult.code === "SUCCESS" && (
                        <Alert severity="success" sx={{ mt: 1 }}>
                            Updated {removeBannedResult.updated ?? 0} of{" "}
                            {removeBannedResult.total ?? 0} puzzles
                            ({removeBannedResult.wordsRemoved ?? 0} words removed
                            {removeBannedResult.rearranged != null
                                ? `, ${removeBannedResult.rearranged} re-arranged`
                                : ""}
                            {removeBannedResult.rearrangeFailed
                                ? `, ${removeBannedResult.rearrangeFailed} rearrange failed`
                                : ""}
                            {removeBannedResult.skipped
                                ? `, ${removeBannedResult.skipped} skipped`
                                : ""}
                            ).
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleRemoveBannedClose} disabled={removeBannedLoading}>
                        {removeBannedResult && !removeBannedResult.error ? "Close" : "Cancel"}
                    </Button>
                    {!removeBannedResult?.code
                        && removeBannedPreview
                        && !removeBannedPreview.error
                        && (removeBannedPreview.updated ?? 0) > 0 && (
                        <Button
                            variant="contained"
                            color="error"
                            onClick={handleRemoveBannedConfirm}
                            disabled={removeBannedLoading}
                        >
                            Remove from {removeBannedPreview.updated} puzzle(s)
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            <Dialog
                open={openRemoveContainedDialog}
                onClose={removeContainedLoading ? undefined : handleRemoveContainedClose}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Remove contained words</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        Within each puzzle, removes a shorter word when its grid path sits entirely on a longer
                        word (e.g. DEW placed on DEWY). Removed words move to not-in-use. Keeps at least 3 words
                        on the grid. Language:{" "}
                        {selectedLanguage === "all" ? "english" : selectedLanguage}.
                    </DialogContentText>
                    {removeContainedLoading && (
                        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                            <CircularProgress size={28} />
                        </Box>
                    )}
                    {removeContainedResult?.error && (
                        <Alert severity="error" sx={{ mt: 1 }}>
                            {removeContainedResult.error}
                        </Alert>
                    )}
                    {removeContainedResult
                        && !removeContainedResult.error
                        && removeContainedResult.code === "SUCCESS" && (
                        <Alert severity="success" sx={{ mt: 1 }}>
                            Updated {removeContainedResult.updated ?? 0} of{" "}
                            {removeContainedResult.total ?? 0} puzzles
                            ({removeContainedResult.wordsRemoved ?? 0} words removed
                            {removeContainedResult.skipped
                                ? `, ${removeContainedResult.skipped} skipped`
                                : ""}
                            ).
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleRemoveContainedClose} disabled={removeContainedLoading}>
                        {removeContainedResult && !removeContainedResult.error ? "Close" : "Cancel"}
                    </Button>
                    {!removeContainedResult?.code && (
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleRemoveContainedRun}
                            disabled={removeContainedLoading}
                            startIcon={removeContainedLoading ? <CircularProgress size={16} color="inherit" /> : null}
                        >
                            Run
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            <ConfirmDialog
                open={openReviewDialog}
                setOpen={setOpenReviewDialog}
                callback={handleConfirmReview}
                title="Mark as Reviewed"
                message={[
                    `Move ${reviewEligibleTemplates.length} selected template(s) to REVIEWED?`,
                    "Draft templates will first move to READY_TO_REVIEW, then REVIEWED with word scores.",
                    "Templates already reviewed or published will be skipped.",
                    "If review fails because a word list overlaps a season puzzle (e.g. Kitchen Counter Words), use Remove duplicates first.",
                ]}
                targetName={`${reviewEligibleTemplates.length} eligible template(s)`}
            />
        </Box>
    );
}
